import { PressRelease } from "@clients/afaseApiClient";
import { ListBlockType_listBlock_manualPostsCollection_items } from "@components/ListBlock/queries/contentfulTypes/ListBlockType";

import { ListItem, ProcessedListItem } from "types/ListItem";

import { ListBlockResultType_pageCollection_items } from "../../components/PageList/queries/contentfulTypes/ListBlockResultType";
import {
  formatDate,
  formatDateDay,
  formatDateDayMonthYear,
  formatDateMonthShort,
  formatEventDate,
  formatPressReleaseDate,
  formatShortEventDate,
  nowDate,
  toDate,
  toDateInStockholmTimeZone,
} from "../dateHelpers";

export const getRealContentTypeIds = (contentTypes: string[]): Array<string> => {
  const realIds = [] as Array<string>;
  contentTypes?.map((item) => {
    switch (item) {
      case "Evenemang":
        realIds.push("eventpage");
        break;
      case "Landningssidor":
        realIds.push("landingpage");
        break;
      case "Försäkringssidor":
        realIds.push("insurancepage");
        break;
      case "Nyhetssidor":
        realIds.push("newspage");
        break;
      case "Rapporter":
        realIds.push("reportpage");
        break;
      case "Standardsidor":
        realIds.push("standardpage");
        break;
      case "Utlysningar":
        realIds.push("standardpage");
        break;
      default:
        break;
    }
  });
  return realIds;
};

export const getTags = (
  page:
    | ListBlockResultType_pageCollection_items
    | ListBlockType_listBlock_manualPostsCollection_items,
): Array<string> => {
  let tag;
  switch (page?.content?.__typename) {
    case "Eventpage":
      const categories: Array<string> = [];
      categories.push(
        page?.content?.eventCategory &&
          page?.content?.eventCategory !== "Ingen specifik typ av event"
          ? page?.content?.eventCategory
          : "Evenemang",
      );
      page?.content?.eventFormat && categories.push(page?.content?.eventFormat);
      return categories;
    case "Reportpage":
      tag =
        page?.content?.reportType && page?.content?.reportType !== "Ingen speciell typ av rapport"
          ? page?.content?.reportType
          : "Rapport";
      return tag.split();
    case "Insurancepage":
      tag = "Försäkring";
      return tag.split();
    case "Newspage":
      tag =
        page?.content?.newsType && page?.content?.newsType !== "Vanlig nyhet"
          ? page?.content?.newsType
          : "Nyhet";
      return tag.split();
    default:
      return Array<string>();
  }
};

export const getPagePublishDate = (
  page:
    | ListBlockResultType_pageCollection_items
    | PressRelease
    | ListBlockType_listBlock_manualPostsCollection_items,
): Date => {
  if ((page as PressRelease)?.publishDate) {
    page = page as PressRelease;
    return toDate(page?.publishDate);
  } else {
    page = page as
      | ListBlockResultType_pageCollection_items
      | ListBlockType_listBlock_manualPostsCollection_items;
    switch (page?.content?.__typename) {
      case "Eventpage":
      case "Newspage":
      case "Reportpage":
        return toDateInStockholmTimeZone(page?.content?.publishDate);
      default:
        return toDate(page?.sys?.publishedAt);
    }
  }
};

export const getEventStartDate = (
  page:
    | ListBlockResultType_pageCollection_items
    | ListBlockType_listBlock_manualPostsCollection_items,
): Date => {
  page = page as
    | ListBlockResultType_pageCollection_items
    | ListBlockType_listBlock_manualPostsCollection_items;
  if (page?.content?.__typename === "Eventpage") {
    return toDateInStockholmTimeZone(page?.content?.startDate);
  }
  return null;
};

export const orderListItems = (items: ListItem[], sortBy: string): Array<ListItem> => {
  switch (sortBy) {
    case "Alfabetisk":
      return items.sort((a, b) => a?.heading?.localeCompare(b?.heading));
    case "Publiceringsdatum":
      return items.sort((a, b) => b?.publishDate?.getTime() - a?.publishDate?.getTime());
    case "Skapandedatum":
      return items.sort((a, b) => b?.createdDate?.getTime() - a?.createdDate?.getTime());
    case "Bara Eventsidor-Startdatum fallande":
      return items.sort((a, b) => b?.startDate?.getTime() - a?.startDate?.getTime());
    case "Bara Eventsidor-Startdatum stigande":
      return items.sort((a, b) => a?.startDate?.getTime() - b?.startDate?.getTime());
    case "Bara Utlysningar - Öppningsdatum":
      return items.sort(
        (a, b) => a?.fundingOpeningDate?.getTime() - b?.fundingOpeningDate?.getTime(),
      );
    case "Bara Utlysningar - Stängningsdatum":
      return items.sort(
        (a, b) => a?.fundingClosingDate?.getTime() - b?.fundingClosingDate?.getTime(),
      );
    default:
      return items;
  }
};

export const stringifyListItems = (items: Array<ListItem>) => {
  const result = new Array<ProcessedListItem>();
  items.forEach((item: ListItem) => {
    const isOngoingEvent = checkOngoingEvent(item?.type, item?.startDate, item?.endDate);
    const dateAndTimeArray = getDateAndTimeArray(
      item.type,
      item?.publishDate,
      item?.startDate,
      item?.endDate,
      isOngoingEvent,
    );
    const dateSignDay = formatDateDay(item?.startDate);
    const dateSignMonth = formatDateMonthShort(item?.startDate);

    result.push({
      heading: item.heading,
      hideDate: item.hidePublishDate,
      categories: item.categories,
      dateSignDay: dateSignDay,
      dateSignMonth: dateSignMonth,
      slug: item?.slug,
      text: item.text,
      type: item.type,
      url: item.url,
      id: item.id,
      imageUrl: item.imageUrl || null,
      imageAltText: item.imageAltText || null,
      ivTeaserImage: item.ivTeaserImage,
      linkText: item.linkText,
      dateAndTimeArray: dateAndTimeArray,
      isOngoingEvent: isOngoingEvent,
      isFundingPageItem: item.isFundingPageItem ?? false,
      fundingOpeningDate: item.isFundingPageItem
        ? formatDate(item.fundingOpeningDate, "D MMMM YYYY")
        : null,
      fundingClosingDate: item.isFundingPageItem
        ? formatDate(item.fundingClosingDate, "D MMMM YYYY [klockan] HH.mm")
        : null,
    });
  });

  return result;
};

const checkOngoingEvent = (type, startDate, endDate) => {
  if (type !== "Eventpage") return null;
  const now = nowDate();
  return startDate > now || (startDate < now && endDate > now);
};

const getDateAndTimeArray = (
  type: string,
  publishDate: Date,
  startDate: Date,
  endDate: Date,
  isOngoingEvent: boolean,
) => {
  return type === "Eventpage"
    ? isOngoingEvent
      ? [formatEventDate(startDate, endDate), formatShortEventDate(startDate, endDate)]
      : [formatDateDayMonthYear(startDate)]
    : type === "pressrelease"
      ? [formatPressReleaseDate(publishDate)]
      : [formatDateDayMonthYear(publishDate)];
};
